import React, { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../hooks/useStores';
import styles from './TagsInput.module.css';

const TagsInput = observer(({
    value = [],
    onChange,
    placeholder = "Добавить тег...",
    disabled = false
}) => {
    const rootStore = useStores();
    const { tags: availableTags } = rootStore;

    // Убеждаемся что value всегда массив
    const safeValue = Array.isArray(value) ? value : [];

    const [inputValue, setInputValue] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredTags, setFilteredTags] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    // Фильтрация доступных тегов
    useEffect(() => {
        if (!inputValue.trim()) {
            setFilteredTags([]);
            setHighlightedIndex(-1);
            return;
        }

        const searchTerm = inputValue.toLowerCase().trim();
        const filtered = availableTags.filter(tag =>
            tag.toLowerCase().includes(searchTerm) &&
            !safeValue.includes(tag)
        );

        setFilteredTags(filtered);
        setHighlightedIndex(-1);
    }, [inputValue, availableTags, safeValue]);

    // Закрытие dropdown при клике вне
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Проверяем что клик был вне контейнера компонента
            const container = inputRef.current?.parentElement?.parentElement;
            if (container && !container.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        // Используем click вместо mousedown для корректной работы onClick в dropdown
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Добавление тега
    const addTag = (tag) => {
        if (!safeValue.includes(tag)) {
            const newValue = [...safeValue, tag];
            onChange(newValue);
        }
        setInputValue('');
        setShowDropdown(false);
        inputRef.current?.focus();
    };

    // Удаление тега
    const removeTag = (tagToRemove) => {
        onChange(safeValue.filter(tag => tag !== tagToRemove));
    };

    // Обработка нажатий клавиш
    const handleKeyDown = (e) => {
        // Обработка Backspace для удаления последнего тега
        if (e.key === 'Backspace' && !inputValue && safeValue.length > 0) {
            removeTag(safeValue[safeValue.length - 1]);
            return;
        }

        // Обработка Enter для добавления тега
        if (e.key === 'Enter') {
            e.preventDefault();
            const trimmedInput = inputValue.trim();

            // Если есть выделенный элемент в dropdown
            if (highlightedIndex >= 0 && filteredTags[highlightedIndex]) {
                addTag(filteredTags[highlightedIndex]);
            }
            // Если есть только один вариант в списке
            else if (filteredTags.length === 1) {
                addTag(filteredTags[0]);
            }
            // Если введен текст, добавляем как новый тег (если его еще нет)
            else if (trimmedInput && !safeValue.includes(trimmedInput)) {
                addTag(trimmedInput);
            }
            return;
        }

        // Навигация по dropdown только если он открыт и есть элементы
        if (showDropdown && filteredTags.length > 0) {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setHighlightedIndex(prev =>
                        prev < filteredTags.length - 1 ? prev + 1 : prev
                    );
                    break;

                case 'ArrowUp':
                    e.preventDefault();
                    setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
                    break;

                case 'Escape':
                    setShowDropdown(false);
                    setInputValue('');
                    break;
            }
        }
    };

    // Обработка изменения ввода
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setShowDropdown(value.trim().length > 0);
    };

    return (
        <div className={styles.container}>
            <div className={styles.tagsContainer}>
                <div className={styles.tagsWrapper}>
                    {safeValue.map(tag => (
                        <div key={tag} className={styles.tag}>
                            <span>{tag}</span>
                            {!disabled && (
                                <button
                                    type="button"
                                    className={styles.removeButton}
                                    onClick={() => removeTag(tag)}
                                    aria-label={`Удалить тег ${tag}`}
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => inputValue.trim() && setShowDropdown(true)}
                    placeholder={safeValue.length === 0 ? placeholder : ''}
                    className={styles.input}
                    disabled={disabled}
                />
            </div>

            {showDropdown && inputValue.trim() && (
                <div ref={dropdownRef} className={styles.dropdown}>
                    {filteredTags.length > 0 ? (
                        filteredTags.map((tag, index) => (
                            <div
                                key={tag}
                                className={`${styles.dropdownItem} ${
                                    index === highlightedIndex ? styles.highlighted : ''
                                }`}
                                onClick={() => addTag(tag)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                {tag}
                            </div>
                        ))
                    ) : (
                        !safeValue.includes(inputValue.trim()) && (
                            <div className={styles.createNew}>
                                Нажмите Enter чтобы создать "{inputValue.trim()}"
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    );
});

export default TagsInput;